import { useMutation, useQuery, useLazyQuery, useApolloClient } from '@vue/apollo-composable';
import { QueryHeatMapUrlListInput } from '@models/insight/useTopHeatmapList';
import gql from 'graphql-tag';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { generateHeatmapToken } from '@utils/common.util';
import dateUtils from '@utils/date.utils';
import { setHeatMap, addProtocol } from '@models/experience/useHeatmap';
import { useAccountInfo } from '@models/account/useAccountInfo';
import { useGuideStore } from '@/pages/master/store/guide.store';
import urlUtils from '@utils/url.utils';

type PeriodEnum = 'MONTHLY';

export interface PteAutoSaveHistoryHeatmapConfigInput {
    profileId: string;
    siteId: string;
    isMergeParameter: boolean;
    pageGroupId: string;
    type: number;
    uid: string;
    url: string;
    period: PeriodEnum;
    periodValue: number;
    isEnabled: boolean;
}

export interface PteShareHeatmapConfigInput {
    profileId: string;
    siteId: string;
    url: string;
    calendarEnabled: boolean;
    historyHeatmapEnabled: boolean;
}

export interface PtiConversionList {
    conversionId: string;
    cvName: string;
}

export interface ConversionInput {
    conversionId: String;
    cvName: String;
}

export interface HomeSelectedConversionInput {
    profileId: string;
    conversions: ConversionInput[];
}

export interface PtiConversionDataResult {
    cv: number;
    cvRate: string;
    conversionId: string;
    cvName: string;
}

const CREATE_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_MUTATION = gql`
    mutation createAutoSaveHistoryHeatmapConfig($input: PteAutoSaveHistoryHeatmapConfigInput!) {
        createAutoSaveHistoryHeatmapConfig(input: $input)
    }
`;

const CREATE_SHARE_HEATMAP_CONFIG_MUTATION = gql`
    mutation createShareHeatmapConfig($input: PteShareHeatmapConfigInput!) {
        createShareHeatmapConfig(input: $input)
    }
`;

const GET_SHARE_HEATMAP_CONFIG_BY_URL_QUERY = gql`
    query getShareHeatmapConfigByUrl($url: String!) {
        getShareHeatmapConfigByUrl(url: $url) {
            profileId
            siteId
            url
            calendarEnabled
            historyHeatmapEnabled
        }
    }
`;

const GET_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_BY_URL_QUERY = gql`
    query getAutoSaveHistoryHeatmapConfigByUrl($url: String!) {
        getAutoSaveHistoryHeatmapConfigByUrl(url: $url) {
            profileId
            siteId
            isMergeParameter
            pageGroupId
            type
            uid
            url
            period
            periodValue
            isEnabled
        }
    }
`;

const GET_CONVERSION_LIST = gql`
    query getHomeConversionList($profileId: String!) {
        getHomeConversionList(profileId: $profileId) {
            conversionId
            cvName
        }
    }
`;

const GET_CONVERSION_DATA = gql`
    query getHomeConversionMetrics($input: HomeMetricsQueryInput!) {
        getHomeConversionMetrics(input: $input) {
            cv
            cvRate
            conversionId
            cvName
        }
    }
`;

const SAVE_CONVERSION = gql`
    mutation saveHomeSelectedConversionList($input: HomeSelectedConversionInput!) {
        saveHomeSelectedConversionList(input: $input)
    }
`;

const HOME_URL_TERMINAL_DETAIL = gql`
    query getHomeUrlTerminalDetail($input: HeatMapUrlListInput!) {
        getHomeUrlTerminalDetail(input: $input) {
            pc
            smartphone
            tablet
            other
        }
    }
`;

export function useHeatmap() {
    const { profileInfo, allProductList, profileProductMenuList, setProfileProductMenu } =
        useProfileInfo();
    const { account } = useAccountInfo();
    const { getStepConfig, updateStepsStatus } = useGuideStore();
    const createShareHeatmapConfig = async (
        params: PteShareHeatmapConfigInput,
        resultCallback?: (status: boolean) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { mutate } = getCreateShareHeatmapConfigMutation();
        const res = await mutate({
            input: params
        });

        if (res?.data?.createShareHeatmapConfig) {
            resultCallback && typeof resultCallback === 'function' && resultCallback(res.data);
        } else {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(res?.data);
        }
    };

    const getShareHeatmapConfigByUrl = (
        heatmapPageUrl: string,
        resultCallback?: (T: PteShareHeatmapConfigInput) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getShareHeatmapConfigByUrl: PteShareHeatmapConfigInput;
        }>(
            GET_SHARE_HEATMAP_CONFIG_BY_URL_QUERY,
            {
                url: heatmapPageUrl
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(data.data.getShareHeatmapConfigByUrl);
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const createAutoSaveHistoryHeatmapConfig = async (
        params: PteAutoSaveHistoryHeatmapConfigInput,
        resultCallback?: (status: boolean) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { mutate } = getCreateAutoSaveHistoryHeatmapConfigMutation();
        const res = await mutate({
            input: params
        });

        if (res?.data?.createAutoSaveHistoryHeatmapConfig) {
            resultCallback && typeof resultCallback === 'function' && resultCallback(res.data);
        } else {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(res?.data);
        }
    };

    const getAutoSaveHistoryHeatmapConfigByUrl = (
        heatmapPageUrl: string,
        resultCallback?: (T: PteAutoSaveHistoryHeatmapConfigInput) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getAutoSaveHistoryHeatmapConfigByUrl: PteAutoSaveHistoryHeatmapConfigInput;
        }>(
            GET_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_BY_URL_QUERY,
            {
                url: heatmapPageUrl
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(data.data.getAutoSaveHistoryHeatmapConfigByUrl);
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const getPtiConversionList = (
        profileId: string,
        resultCallback?: (T: PtiConversionList[]) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getHomeConversionList: PtiConversionList[];
        }>(
            GET_CONVERSION_LIST,
            {
                profileId
            },
            {
                fetchPolicy: 'cache-and-network'
            }
        );

        onResult(data => {
            resultCallback &&
                typeof resultCallback === 'function' &&
                resultCallback(data?.data?.getHomeConversionList || []);
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const getPtiConversionData = (
        uid: string,
        profileId: string,
        startTime: string, // "2022/06/01"
        endTime: string, // "2022/06/01"
        resultCallback?: (T: PtiConversionDataResult[]) => void,
        rejectCallback?: (err: any) => void
    ) => {
        const { onResult, onError } = useQuery<{
            getHomeConversionMetrics: PtiConversionDataResult[];
        }>(
            GET_CONVERSION_DATA,
            {
                input: {
                    profileId,
                    accountId: uid,
                    startTime,
                    endTime
                }
            },
            {
                fetchPolicy: 'cache-and-network'
            }
        );

        onResult(data => {
            if (data?.data?.getHomeConversionMetrics) {
                resultCallback &&
                    typeof resultCallback === 'function' &&
                    resultCallback(data?.data?.getHomeConversionMetrics || []);
            }
        });

        onError(err => {
            rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
        });
    };

    const openOverlayHeatmap = (urlItem: any, successCallback?: () => void) => {
        if (!urlItem || !urlItem?.url || !profileInfo.value?.sid) return;
        let deviceType = 'MOBILE';
        const { url, pid, isToday } = urlItem;
        const { sid, timeZone } = profileInfo.value;
        const urlSeparateArr = url?.split('?');
        const _rangeType = urlSeparateArr?.[1] ? 'URL' : 'MERGE_URL';
        const _currentUrl = _rangeType === 'MERGE_URL' ? urlSeparateArr?.[0] : url;
        const token = generateHeatmapToken(sid as string, url);
        const { startTime, endTime } = isToday
            ? dateUtils.getDate('today', timeZone, null)
            : dateUtils.getDate('past', timeZone, {
                  value: isToday ? 0 : 7,
                  isIncludeToday: true
              });

        const options: QueryHeatMapUrlListInput = {
            startTime: startTime as string,
            endTime: endTime as string,
            profileId: sid,
            urlType: urlSeparateArr?.[1] ? 'original' : 'combined',
            searchItem: _currentUrl
        };
        getHomeUrlTerminalDetail(
            options,
            res => {
                const newData = [];
                for (let i in res) {
                    newData.push({
                        key: i,
                        value: res[i]
                    });
                }
                const max = newData.sort((a, b) => {
                    return b.value - a.value;
                })[0];
                if (max && max.value > 0) {
                    if (max.key && max.key !== 'smartphone') {
                        deviceType = max.key.toUpperCase();
                    }
                }
                const option = {
                    dataParameter: {
                        startTime: dateUtils.getTimeStampWithTimezone(
                            startTime as string,
                            timeZone,
                            false,
                            false
                        ),
                        endTime: dateUtils.getTimeStampWithTimezone(
                            endTime as string,
                            timeZone,
                            false,
                            false
                        ),
                        timeZone: timeZone,
                        urlParam: _rangeType === 'MERGE_URL' || !pid ? _currentUrl : pid,
                        rangeType: _rangeType,
                        filter: [],
                        sid,
                        deviceType,
                        paramList: []
                    },
                    pageInfo: {
                        display: { readline: true, showHide: false },
                        heatmapType: 'click',
                        currentUrl: _currentUrl,
                        fromCampaign: false,
                        deviceTypeName: deviceType,
                        pageOrientation: 'ALL', //横竖屏类型
                        pageGroupInfo: {},
                        filterInfo: [],
                        shortcutType: isToday
                            ? {
                                  label: ['Today', '今天', '今日'],
                                  value: {
                                      type: 'today'
                                  }
                              }
                            : {
                                  label: ['Past 7 days', '过去7天', '過去7日間'],
                                  value: {
                                      type: 'custom_day',
                                      includeToday: true,
                                      number: 7
                                  }
                              }
                    }
                };
                setHeatMap(option, token).then((pageUrl?: string) => {
                    if (!pageUrl) return;

                    const language = account.value?.language?.toLocaleLowerCase();
                    const urlParam = `ptengine_heatmap_token=${token}${
                        language ? '&pt_lang=' + language : ''
                    }`;
                    const overlayUrl = urlUtils.urlInsertParam(addProtocol(pageUrl), urlParam);
                    urlUtils.openUrlUseATag(overlayUrl);
                    !getStepConfig('startGuide_heatmap')?.done &&
                        updateStepsStatus(['startGuide_heatmap']);
                    successCallback && typeof successCallback === 'function' && successCallback();
                });
            },
            err => {
                console.log(err);
            }
        );
    };

    return {
        createShareHeatmapConfig,
        getShareHeatmapConfigByUrl,
        getPtiConversionList,
        getPtiConversionData,
        createAutoSaveHistoryHeatmapConfig,
        getAutoSaveHistoryHeatmapConfigByUrl,
        openOverlayHeatmap
    };
}

export async function saveConversionList(
    uid: string,
    profileId: string,
    conversions: PtiConversionList[]
) {
    const { mutate } = useMutation(SAVE_CONVERSION, {
        fetchPolicy: 'no-cache'
    });
    const res = await mutate({
        input: {
            accountId: uid,
            profileId,
            conversions
        }
    });
    return res;
}

function getCreateAutoSaveHistoryHeatmapConfigMutation() {
    return useMutation(CREATE_AUTO_SAVE_HISTORY_HEATMAP_CONFIG_MUTATION, {
        fetchPolicy: 'no-cache'
    });
}

function getCreateShareHeatmapConfigMutation() {
    return useMutation(CREATE_SHARE_HEATMAP_CONFIG_MUTATION, {
        fetchPolicy: 'no-cache'
    });
}

export async function getHomeUrlTerminalDetail(
    params?: QueryHeatMapUrlListInput,
    resultCallback?: (T: QueryHeatMapUrlListInput) => void,
    rejectCallback?: (err: any) => void
) {
    const { onError, onResult } = useQuery<{ getHomeUrlTerminalDetail: QueryHeatMapUrlListInput }>(
        HOME_URL_TERMINAL_DETAIL,
        { input: params },
        {
            fetchPolicy: 'no-cache' //cache-and-network cache-first
        }
    );
    onResult(data => {
        resultCallback &&
            typeof resultCallback === 'function' &&
            resultCallback(data?.data?.getHomeUrlTerminalDetail as QueryHeatMapUrlListInput);
    });

    onError(err => {
        rejectCallback && typeof rejectCallback === 'function' && rejectCallback(err);
    });
}

export async function getUrlTerminalDetail(params?: QueryHeatMapUrlListInput) {
    const client = useApolloClient();
    const id = params?.id;
    delete params?.id;
    const { data } = await client.client.query({
        query: HOME_URL_TERMINAL_DETAIL,
        variables: { input: params }
    });
    return {
        id,
        data: data.getHomeUrlTerminalDetail || false
    };
}
